import { Toolbar, ToolbarButton, ToolbarSeparator } from "@prismicio/editor-ui"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import type { Editor } from "@tiptap/core"
import {
  BubbleMenu as TiptapBubbleMenu,
  type BubbleMenuProps as TiptapBubbleMenuProps,
} from "@tiptap/react"
import { useEffect, useState } from "react"

import {
  ALL_EXTRA_ITEMS,
  ALL_MARK_ITEMS,
  ALL_NODE_ITEMS,
  filterMenuItems,
} from "../models/MenuItems"
import { ExtraItems } from "./ExtraItems"
import { LabelItems } from "./Label/LabelItems"
import { LabelPopover } from "./Label/LabelPopover"
import { LinkItems } from "./Link/LinkItems"
import { LinkPopover } from "./Link/LinkPopover"
import { SetDocumentLinkModal } from "./Link/SetDocumentLinkModal"
import { SetMediaLinkModal } from "./Link/SetMediaLinkModal"
import { SetWebLinkModal } from "./Link/SetWebLinkModal"
import { MarkItems } from "./MarkItems"
import { NodeItems } from "./NodeItems"

interface BubbleMenuProps {
  editor: Editor
  nodeTypes: string[]
  labels: string[]
}

function getOverlayOptions(
  offset: [number, number],
): TiptapBubbleMenuProps["tippyOptions"] {
  return {
    duration: 100,
    hideOnClick: true,
    offset: offset,
    placement: "top-start",
    // NOTE: "appendTo" appends the BubbleMenu to our highest div.
    // This prevents it from rendering below other elements.
    // Ideally we would use document.body (what Radix uses), but it doesn't work.
    appendTo: document.querySelector("body > div") ?? "parent",
  }
}

export type Modal = "webLink" | "documentLink" | "mediaLink" | undefined

export function BubbleMenu(props: BubbleMenuProps) {
  const { editor, nodeTypes, labels } = props

  const [modal, setModal] = useState<Modal>()

  const hasLink = nodeTypes.includes(RichTextNodeType.hyperlink)

  const displayHoverMenu = !modal

  const { shouldShowBubbleMenu, shouldshowPopover } = useBubbleMenu({
    editor,
  })

  // Tiptap sucks and they briefly show the BubbleMenu at first,
  // albeit with visibility: hidden but it still takes space and causes a layout shift.
  // And OF COURSE, they don't call shouldShowBubbleMenu at that time.
  // Note that we want to rewrite BubbleMenu anyway and this is an easy requirement to add.
  const [forceHide, setForceHide] = useState(true)
  useEffect(() => {
    setForceHide(false)
  }, [])

  function onWebModalOpenChange(open: boolean) {
    setModal(open ? "webLink" : undefined)
  }

  function onDocumentModalOpenChange(open: boolean) {
    setModal(open ? "documentLink" : undefined)
  }

  function onMediaPickerOpenChange(open: boolean) {
    setModal(open ? "mediaLink" : undefined)
  }

  if (forceHide) return null

  return (
    <>
      <TiptapBubbleMenu
        editor={editor}
        tippyOptions={getOverlayOptions([-48, 0])}
        shouldShow={shouldShowBubbleMenu}
      >
        {displayHoverMenu && (
          <BubbleMenuToolbar
            editor={editor}
            nodeTypes={nodeTypes}
            labels={labels}
            setModal={setModal}
          />
        )}
      </TiptapBubbleMenu>

      <TiptapBubbleMenu
        editor={editor}
        tippyOptions={getOverlayOptions([-48, 0])}
        shouldShow={(args) => shouldshowPopover(args, "hyperlink")}
      >
        {displayHoverMenu && (
          <LinkPopover
            editor={editor}
            onEditWebLink={() => setModal("webLink")}
            onEditDocumentLink={() => setModal("documentLink")}
            onEditMediaLink={() => setModal("mediaLink")}
          />
        )}
      </TiptapBubbleMenu>

      <TiptapBubbleMenu
        editor={editor}
        tippyOptions={getOverlayOptions([0, 0])}
        shouldShow={(args) => shouldshowPopover(args, "label")}
      >
        <LabelPopover editor={editor} />
      </TiptapBubbleMenu>

      {hasLink && (
        <>
          <SetWebLinkModal
            open={modal === "webLink"}
            setOpen={onWebModalOpenChange}
            editor={editor}
          />

          <SetDocumentLinkModal
            open={modal === "documentLink"}
            setOpen={onDocumentModalOpenChange}
            editor={editor}
          />

          <SetMediaLinkModal
            open={modal === "mediaLink"}
            setOpen={onMediaPickerOpenChange}
            editor={editor}
          />
        </>
      )}
    </>
  )
}

interface BubbleMenuToolbarProps {
  editor: Editor
  nodeTypes: string[]
  labels: string[]
  setModal: (modal: Modal) => void
}

function BubbleMenuToolbar(props: BubbleMenuToolbarProps) {
  const { editor, nodeTypes, labels, setModal } = props

  const hasLink = nodeTypes.includes("hyperlink")
  const hasLabels = labels.length > 0
  const nodeItems = filterMenuItems(ALL_NODE_ITEMS, nodeTypes)
  const markItems = filterMenuItems(ALL_MARK_ITEMS, nodeTypes)
  const extraItems = filterMenuItems(ALL_EXTRA_ITEMS, nodeTypes)

  const toolbarItemsCount =
    nodeItems.length + markItems.length + extraItems.length + Number(hasLink)

  if (toolbarItemsCount === 0) return null

  return (
    <Toolbar>
      <NodeItems items={nodeItems} editor={editor} />
      {hasLabels && <LabelItems labels={labels} editor={editor} />}
      <MarkItems items={markItems} editor={editor} />
      {hasLink && <LinkItems editor={editor} setModal={setModal} />}
      <ExtraItems items={extraItems} editor={editor} />
      {toolbarItemsCount > 1 && (
        <>
          <ToolbarSeparator />
          <ToolbarButton
            key="clear"
            onClick={() => {
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }}
            startIcon="formatClear"
          />
        </>
      )}
    </Toolbar>
  )
}

interface useBubbleMenuProps {
  editor: Editor
}

type ShouldShowArgs = Parameters<
  NonNullable<TiptapBubbleMenuProps["shouldShow"]>
>[0]

function useBubbleMenu(props: useBubbleMenuProps) {
  const { editor } = props

  function shouldShowBubbleMenu(args: ShouldShowArgs) {
    const { view, state, from, to } = args

    if (!view.hasFocus()) return false

    const { doc, selection } = state

    if (editor.isActive(RichTextNodeType.preformatted)) return false

    const isEmpty = selection.empty || doc.textBetween(from, to).length === 0

    if (isEmpty) return false

    return true
  }

  function shouldshowPopover(
    shouldShowArgs: ShouldShowArgs,
    markName: "label" | "hyperlink",
  ) {
    const { view, state, from, to } = shouldShowArgs
    const node = view.state.doc.nodeAt(from)
    const { doc, selection } = state

    const isNotEmpty = !selection.empty || doc.textBetween(from, to).length > 0

    if (isNotEmpty) return false

    if (node?.marks.some((mark) => mark.type.name === markName)) return true

    return false
  }

  return {
    shouldShowBubbleMenu,
    shouldshowPopover,
  }
}
